import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ConfidenceSummary from "../components/ConfidenceSummary";
import FieldReconciliationWizard from "../components/FieldReconciliationWizard";
import DisclosureQuestionnaire from "../components/DisclosureQuestionnaire";
import DocumentFolder from "../components/DocumentFolder";
import ReassurancePanel from "../components/ReassurancePanel";
import SecurityTrustStrip from "../components/SecurityTrustStrip";
import TermsAcceptanceModal from "../components/TermsAcceptanceModal";
import Card from "../components/ui/Card";
import { getVisibleDocumentTypes } from "../config/documentTypes";
import { getAllTA6Questions } from "../config/ta6Questions";
import { getAllTA7Questions } from "../config/ta7Questions";
import { getAllLPE1Questions } from "../config/lpe1Questions";
import { api, ApiClientError } from "../utils/api";
import { trackEvent } from "../utils/analytics";
import { useAuth } from "../context/AuthContext";
import { useTermsAcceptance } from "../hooks/useTermsAcceptance";

// Step 1 (the old "Confirm property basics" form) was dissolved in
// Phase B2.5 (tenure-on-CTA). The wizard now starts at Step 1 = Disclosure.
// Internal numbering 1-3 maps to: 1=Disclosure, 2=Upload, 3=Review.
type Step = 1 | 2 | 3;

interface UploadedDocument {
  id: string;
  documentTypeId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  s3Key?: string;
  status: 'pending' | 'uploading' | 'complete' | 'failed';
  progress: number;
  error?: string;
  retryCount: number;
  uploadedAt?: string;
  file?: File;
}

interface FormData {
  role: "seller" | "buyer";
  tenure: "leasehold" | "freehold";
  alterationsMade?: boolean;
  address?: { line1: string; line2?: string; city: string; postcode: string };
  documents: UploadedDocument[];
  disclosureAnswers: Record<string, string>;
  uploadSessionId: string;
  createdAt: string;
}

const STORAGE_KEY = 'selling_documents_progress';

/**
 * "Meaningfully edited" = the user has actually answered at least one
 * questionnaire question, uploaded a document, or set a non-default
 * tenure/address. Used to gate server-side saves so we don't write the
 * default-empty FormData on every mount.
 */
function isMeaningfullyEdited(data: Pick<FormData, 'address' | 'documents' | 'disclosureAnswers'>): boolean {
  if (data.address) return true;
  if (data.documents && data.documents.length > 0) return true;
  if (data.disclosureAnswers && Object.keys(data.disclosureAnswers).length > 0) return true;
  return false;
}

function hasMeaningfulFormData(formData: Record<string, unknown>): boolean {
  if (formData.address) return true;
  if (Array.isArray(formData.documents) && formData.documents.length > 0) return true;
  if (formData.disclosureAnswers && typeof formData.disclosureAnswers === 'object'
      && Object.keys(formData.disclosureAnswers as Record<string, unknown>).length > 0) return true;
  return false;
}

interface StepConfig {
  num: number;
  label: string;
  sublabel?: string;
}

function Stepper({ currentStep }: { currentStep: Step }) {
  const steps: StepConfig[] = [
    { num: 1, label: "Disclosure questionnaire" },
    { num: 2, label: "Upload documents", sublabel: "optional but recommended" },
    { num: 3, label: "Review and pay" }
  ];
  return (
    <nav aria-label="Progress steps" className="mt-8">
      <ol className="flex items-start justify-between gap-2 sm:gap-0 sm:items-center">
        {steps.map((step, index) => {
          const isComplete = currentStep > step.num;
          const isCurrent = currentStep === step.num;
          const isFuture = currentStep < step.num;
          return (
            <li key={step.num} className="flex items-center flex-1 min-w-0">
              <div className="flex flex-col items-center min-w-0">
                <span className={`inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full text-xs sm:text-sm font-bold shrink-0 ${isComplete ? "bg-emerald-700 text-white" : isCurrent ? "bg-emerald-700 text-white ring-4 ring-emerald-100" : "bg-slate-200 text-slate-600"}`}>
                  {isComplete ? <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg> : step.num}
                </span>
                <span className={`mt-2 text-[11px] sm:text-xs font-medium text-center leading-tight ${isFuture ? "text-slate-600" : "text-slate-900"}`}>{step.label}</span>
                {step.sublabel && <span className="text-[10px] sm:text-xs text-slate-600 text-center hidden sm:block">{step.sublabel}</span>}
                {isCurrent && <span className="text-[10px] sm:text-xs text-emerald-700 font-medium">You are here</span>}
              </div>
              {index < steps.length - 1 && <div className={`w-8 sm:w-24 h-1 mx-1 sm:mx-2 rounded shrink-0 ${isComplete ? "bg-emerald-700" : "bg-slate-200"}`} />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}


function SignInGate({ onSignIn }: { onSignIn: () => void }) {
  const [showWhyModal, setShowWhyModal] = useState(false);

  // Close modal on Escape
  useEffect(() => {
    if (!showWhyModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowWhyModal(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showWhyModal]);

  return (
    <>
      <Card className="mt-6">
        <h3 className="text-lg font-semibold text-slate-950">Sign in to save your draft across devices</h3>
        <p className="mt-2 text-slate-700">Takes 30 seconds, no password — we email you a one-time code.</p>
        <div className="mt-4 flex items-center gap-4">
          <button onClick={onSignIn} className="btn-primary">Sign in</button>
          <button onClick={() => setShowWhyModal(true)} className="text-sm text-slate-600 hover:text-slate-900 underline">Why sign in?</button>
        </div>
      </Card>
      {showWhyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="max-w-md mx-4" role="dialog" aria-modal="true" aria-labelledby="why-signin-title">
            <h3 id="why-signin-title" className="text-lg font-semibold">Why sign in?</h3>
            <p className="mt-3 text-sm text-slate-700">
              The TA6 questionnaire takes 15&ndash;30 minutes. Signing in means your draft is saved to your account, not just to this browser. You can finish from another device, and your uploaded documents are linked to your account for download later.
            </p>
            <div className="mt-4 flex justify-end"><button onClick={() => setShowWhyModal(false)} className="btn-secondary">Close</button></div>
          </Card>
        </div>
      )}
    </>
  );
}


export default function SellingDocuments() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // propertyId is set when this component is mounted on the new canonical
  // route /account/property/:propertyId/seller-pack. The legacy
  // /selling-documents route doesn't have a propertyId param. When set, we
  // fetch the address from /api/property/:propertyId so the user doesn't
  // re-enter what they already confirmed on the property file.
  const { propertyId } = useParams<{ propertyId?: string }>();
  const packId = searchParams.get('packId');
  const isPaidParam = searchParams.get('paid') === 'true';
  // The path to return to after OTP login. When mounted on the canonical
  // route, preserve the propertyId so the wizard re-loads in place.
  const returnToPath = propertyId
    ? `/account/property/${propertyId}/seller-pack`
    : '/selling-documents';
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const { showModal: showTermsModal, checkAcceptance, handleAccept: acceptTerms, handleDecline: declineTerms, isAccepting, error: termsError } = useTermsAcceptance();
  const [step, setStep] = useState<Step>(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [markUnavailableError, setMarkUnavailableError] = useState<string | null>(null);
  const [recoveryBanner, setRecoveryBanner] = useState(false);
  const [packIsPaid, setPackIsPaid] = useState(isPaidParam);
  const [formattedPairs, setFormattedPairs] = useState<{ questionId: string; raw: string; formatted: string | null }[] | undefined>();
  const [acceptanceDecisions, setAcceptanceDecisions] = useState<Record<string, 'raw' | 'formatted'>>({});
  const [isFormatting, setIsFormatting] = useState(false);
  const [formData, setFormData] = useState<FormData>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return { ...parsed, uploadSessionId: parsed.uploadSessionId || `session-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`, createdAt: parsed.createdAt || new Date().toISOString(), alterationsMade: parsed.alterationsMade ?? false, disclosureAnswers: parsed.disclosureAnswers || {} };
      } catch { /* ignore */ }
    }
    return { role: "seller", tenure: "freehold", alterationsMade: false, documents: [], disclosureAnswers: {}, uploadSessionId: `session-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`, createdAt: new Date().toISOString() };
  });
  const [markedUnavailable, setMarkedUnavailable] = useState<Set<string>>(() => {
    const saved = localStorage.getItem(`${STORAGE_KEY}_unavailable`);
    if (saved) { try { return new Set(JSON.parse(saved)); } catch { return new Set(); } }
    return new Set();
  });

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(formData)); }, [formData]);

  // ── Server-side draft persistence (Phase B3b) ─────────────────────────
  // Three states tracked: a one-time "hydrated from server" flag (we only
  // attempt the GET once per mount), the most recent saved-at timestamp
  // (used by the UI indicator), and a save-error flag.
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [draftSaveError, setDraftSaveError] = useState(false);

  // Phase B2: alterationsMade is derived from the TA6 §4.1 questionnaire
  // answer ("Have any alterations or additions been made to the property?")
  // rather than a separate Step-1 radio. The Step-1 radio was a duplicate of
  // the same TA6 question. Returning sessions still have the legacy
  // formData.alterationsMade boolean; we read whichever is most informative.
  const alterationsMade = formData.disclosureAnswers['ta6-4.1'] === 'yes'
    ? true
    : formData.disclosureAnswers['ta6-4.1'] === 'no'
      ? false
      : (formData.alterationsMade ?? false);
  useEffect(() => { localStorage.setItem(`${STORAGE_KEY}_unavailable`, JSON.stringify(Array.from(markedUnavailable))); }, [markedUnavailable]);

  // Address handoff from PropertyFile → Seller's Pack.
  //
  // Two paths: the new canonical route /account/property/:propertyId/seller-pack
  // gives us a propertyId — we fetch the address from /api/property/:propertyId.
  // The legacy /selling-documents?address=... route still works as a back-compat
  // alias by parsing the comma-separated address string.
  //
  // Pre-fill on first load only; never overwrite a previously-saved address.
  useEffect(() => {
    if (formData.address) return undefined;
    if (propertyId) {
      let cancelled = false;
      void (async () => {
        try {
          const result = await api.getPropertyFile(propertyId);
          if (cancelled || !result.address) return;
          setFormData(prev => prev.address ? prev : { ...prev, address: result.address });
        } catch {
          // Silent — user can still enter the address manually on Step 1.
        }
      })();
      return () => { cancelled = true; };
    }
    const addressParam = searchParams.get('address');
    if (!addressParam) return undefined;
    const parts = addressParam.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length < 2) return undefined;
    setFormData(prev => ({
      ...prev,
      address: {
        line1: parts[0],
        line2: parts.length > 3 ? parts.slice(1, -2).join(', ') : undefined,
        city: parts[parts.length - 2] || '',
        postcode: parts[parts.length - 1] || '',
      },
    }));
    return undefined;
  }, [propertyId, searchParams, formData.address]);

  // Phase B2.5: Tenure handoff from PropertyFile CTA via ?tenure=…
  //
  // Sync once on mount when the user has NOT meaningfully edited their draft
  // — that prevents clobbering a returning seller's saved tenure with a
  // freshly clicked CTA value. Disclosure answers and uploaded docs both
  // count as meaningful edits via isMeaningfullyEdited.
  useEffect(() => {
    const tenureParam = searchParams.get('tenure');
    if (tenureParam !== 'freehold' && tenureParam !== 'leasehold') return;
    setFormData(prev => {
      if (prev.tenure === tenureParam) return prev;
      if (isMeaningfullyEdited(prev)) return prev;
      return { ...prev, tenure: tenureParam };
    });
  }, [searchParams]);

  // Phase B3b: Hydrate from the server-side draft on mount.
  //
  // If the user is signed in AND we're on the propertyId-anchored route,
  // try to fetch the saved draft. If one exists, merge its formData into
  // local state — but never clobber localStorage data the user has already
  // started filling in (they may have started a draft anonymously then
  // signed in; the migration effect below handles that case).
  //
  // Runs once per mount. Failure is silent — user keeps editing locally,
  // the debounced save will retry.
  useEffect(() => {
    if (draftHydrated || !isAuthenticated || !propertyId) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const draft = await api.getSellerDraft(propertyId);
        if (cancelled) return;
        if (draft && hasMeaningfulFormData(draft.formData)) {
          // Server draft wins ONLY if local hasn't accumulated meaningful
          // edits since the user landed. Otherwise the migration effect
          // will push local up to the server.
          setFormData(prev => isMeaningfullyEdited(prev) ? prev : { ...prev, ...(draft.formData as Partial<FormData>) });
          setDraftSavedAt(draft.updatedAt);
        }
      } catch {
        // Silent — debounced save will retry.
      } finally {
        if (!cancelled) setDraftHydrated(true);
      }
    })();
    return () => { cancelled = true; };
  }, [draftHydrated, isAuthenticated, propertyId]);

  // Phase B3b: Debounced PUT on every meaningful formData change.
  //
  // Skips: pre-hydration (so we don't clobber a server draft we haven't
  // read yet), unauthenticated users (anonymous flow stays localStorage-
  // only), and routes without propertyId (the legacy /selling-documents
  // alias has no anchor to save against).
  //
  // 1500ms debounce balances responsiveness vs request volume on a 59-
  // question form.
  useEffect(() => {
    if (!draftHydrated || !isAuthenticated || !propertyId) return undefined;
    if (!isMeaningfullyEdited(formData)) return undefined;
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const draft = await api.putSellerDraft(propertyId, formData as unknown as Record<string, unknown>);
          setDraftSavedAt(draft.updatedAt);
          setDraftSaveError(false);
        } catch {
          setDraftSaveError(true);
        }
      })();
    }, 1500);
    return () => clearTimeout(handle);
  }, [draftHydrated, isAuthenticated, propertyId, formData]);

  // Recover stale upload states (21.1): if browser was closed mid-upload, reset stuck documents
  useEffect(() => {
    const staleStatuses = ['uploading', 'pending'] as const;
    const stuckDocs = formData.documents.filter(d => staleStatuses.includes(d.status as any));
    if (stuckDocs.length > 0) {
      setFormData(prev => ({
        ...prev,
        documents: prev.documents.map(d =>
          staleStatuses.includes(d.status as any)
            ? { ...d, status: 'failed' as const, progress: 0, error: 'Upload was interrupted' }
            : d
        ),
      }));
      setRecoveryBanner(true);
    }
  }, []);

  // Returning user: packId in URL or existing uploaded documents → land on
  // the upload step so the folder is the first thing they see.
  useEffect(() => {
    if (packId) {
      setStep(2);
      // Fetch pack state from server to reconcile
      api.getSessionState(packId).then(({ session }) => {
        setPackIsPaid(false); // Session endpoint doesn't expose isPaid; default to active
        if (session.documents?.length > 0) {
          const serverDocs = session.documents.map((d: any) => ({
            id: d.id || d.documentId,
            documentTypeId: d.documentTypeId,
            fileName: d.fileName,
            fileType: d.fileType || 'application/pdf',
            fileSize: d.fileSize || 0,
            s3Key: d.s3Key,
            status: 'complete' as const,
            progress: 100,
            retryCount: 0,
            uploadedAt: d.uploadedAt,
          }));
          updateFormData({
            documents: serverDocs,
            uploadSessionId: session.sessionId,
            tenure: session.tenure,
            alterationsMade: session.alterationsMade,
            address: session.address,
            createdAt: session.createdAt,
          });
        }
      }).catch(() => {
        // If fetch fails, fall back to local state
      });
    } else if (formData.documents.some(d => d.status === 'complete')) {
      setStep(2);
    }
  }, [packId]);

  const updateFormData = (updates: Partial<FormData>) => setFormData(prev => ({ ...prev, ...updates }));

  // All document mutations use functional setFormData updates. Reading the
  // live `prev.documents` (rather than the closed-over `formData.documents`)
  // is essential here: handleFileSelect and uploadDocument fire two state
  // updates back-to-back within the same render, so the second update would
  // otherwise map over the stale pre-insert list and silently drop the row.
  const updateDocuments = (fn: (docs: UploadedDocument[]) => UploadedDocument[]) =>
    setFormData(prev => ({ ...prev, documents: fn(prev.documents) }));

  const handleFileSelect = async (file: File, documentTypeId: string) => {
    const newDoc: UploadedDocument = { id: `${Date.now()}-${Math.random()}`, documentTypeId, fileName: file.name, fileType: file.type, fileSize: file.size, status: 'pending', progress: 0, retryCount: 0, uploadedAt: new Date().toISOString(), file };
    updateDocuments(docs => [...docs, newDoc]);
    await uploadDocument(newDoc.id, file, documentTypeId);
  };

  const uploadDocument = async (docId: string, file: File, documentTypeId: string) => {
    updateDocuments(docs => docs.map(doc => doc.id === docId ? { ...doc, status: 'uploading' as const } : doc));
    try {
      const result = await api.uploadSellingDocument(formData.uploadSessionId, documentTypeId, file);
      if (result.uploadUrl) {
        const uploadResponse = await fetch(result.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        if (!uploadResponse.ok) throw new Error('S3 upload failed');
      }
      updateDocuments(docs => docs.map(doc => doc.id === docId ? { ...doc, id: result.document.id, status: 'complete' as const, progress: 100, s3Key: result.document.s3Key, uploadedAt: result.document.uploadedAt } : doc));
      trackEvent({ name: 'document_uploaded', properties: { documentTypeId } });
    } catch (error) {
      updateDocuments(docs => docs.map(doc => doc.id === docId ? { ...doc, status: 'failed' as const, error: error instanceof Error ? error.message : 'Upload failed' } : doc));
    }
  };

  const retryUpload = async (docId: string) => {
    const doc = formData.documents.find(d => d.id === docId);
    if (!doc || doc.retryCount >= 3 || !doc.file) return;
    updateDocuments(docs => docs.map(d => d.id === docId ? { ...d, status: 'pending' as const, error: undefined, retryCount: d.retryCount + 1 } : d));
    await uploadDocument(docId, doc.file, doc.documentTypeId);
  };

  const removeDocument = async (docId: string) => {
    const doc = formData.documents.find(d => d.id === docId);
    if (doc?.status === 'complete' && doc.s3Key) { try { await api.deleteSellingDocument(formData.uploadSessionId, docId); } catch { /* ignore */ } }
    updateDocuments(docs => docs.filter(d => d.id !== docId));
  };

  const handleMarkUnavailable = async (documentTypeId: string) => {
    const newSet = new Set(markedUnavailable);
    if (!newSet.has(documentTypeId)) {
      try { await api.markDocumentUnavailable(formData.uploadSessionId, documentTypeId); newSet.add(documentTypeId); trackEvent({ name: 'document_marked_unavailable', properties: { documentTypeId } }); }
      catch (error) { setMarkUnavailableError(error instanceof ApiClientError ? error.message : 'Failed to mark document as unavailable'); return; }
    } else { newSet.delete(documentTypeId); }
    setMarkedUnavailable(newSet);
  };

  const clearProgress = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(`${STORAGE_KEY}_unavailable`);
    setFormData({ role: "seller", tenure: "freehold", alterationsMade: false, documents: [], disclosureAnswers: {}, uploadSessionId: `session-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`, createdAt: new Date().toISOString() });
    setMarkedUnavailable(new Set());
    setFormattedPairs(undefined);
    setAcceptanceDecisions({});
    setStep(1);
  };

  // Trigger answer formatting when entering Step 4 (preview)
  const triggerFormatting = async () => {
    if (formattedPairs || isFormatting) return; // Already formatted or in progress
    const answersToFormat = formData.disclosureAnswers;
    if (Object.keys(answersToFormat).length === 0) return;

    // Build question context from all question configs
    const allQuestions = [
      ...getAllTA6Questions().map(q => ({ questionId: q.id, questionText: q.questionText, sectionTitle: `TA6 Section ${q.section}` })),
      ...(formData.tenure === 'leasehold' ? [
        ...getAllTA7Questions().map(q => ({ questionId: q.id, questionText: q.questionText, sectionTitle: `TA7 Section ${q.section}` })),
        ...getAllLPE1Questions().map(q => ({ questionId: q.id, questionText: q.questionText, sectionTitle: `LPE1 Section ${q.section}` })),
      ] : []),
    ];

    setIsFormatting(true);
    try {
      const result = await api.formatDisclosureAnswers(formData.uploadSessionId, answersToFormat, allQuestions);
      if (result.success) {
        setFormattedPairs(result.pairs);
        // Default all formatted answers to 'formatted'
        const defaults: Record<string, 'raw' | 'formatted'> = {};
        for (const pair of result.pairs) {
          if (pair.formatted) {
            defaults[pair.questionId] = 'formatted';
          }
        }
        setAcceptanceDecisions(defaults);
      }
    } catch (error) {
      // Formatting is optional — if it fails, seller proceeds with raw answers
      console.warn('Answer formatting failed:', error);
    } finally {
      setIsFormatting(false);
    }
  };

  const handleGenerateCompliancePack = async () => {
    const accepted = await checkAcceptance();
    if (!accepted) return;
    setIsGenerating(true);
    setGenerationError(null);
    trackEvent({ name: 'selling_payment_started' });
    try {
      const addressStr = formData.address
        ? `${formData.address.line1}${formData.address.line2 ? ', ' + formData.address.line2 : ''}, ${formData.address.city}, ${formData.address.postcode}`
        : undefined;
      const result = await api.generateCompliancePack(
        formData.uploadSessionId,
        {
          disclosureAnswers: Object.keys(formData.disclosureAnswers).length > 0 ? formData.disclosureAnswers : undefined,
          address: addressStr,
          tenure: formData.tenure,
          acceptanceDecisions: Object.keys(acceptanceDecisions).length > 0 ? acceptanceDecisions : undefined,
        }
      );
      if (result.success) {
        // Hand off to Stripe Checkout. The order-confirmation page is reached via
        // Stripe's success_url redirect (which carries {CHECKOUT_SESSION_ID}); we do
        // NOT navigate there directly from here. selling_payment_completed now fires
        // from the webhook-driven fulfilment path, not from this click handler.
        const session = await api.createCheckoutSession({
          productType: 'compliance_pack',
          productId: result.pack.packId,
        });
        // Do NOT clear isGenerating on success — the page is about to unload and
        // flipping the button back to enabled briefly would cause a UX blip.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        window.location.assign(session.url);
      }
    } catch (error) {
      trackEvent({ name: 'selling_payment_failed', properties: { reason: error instanceof Error ? error.message : 'unknown' } });
      setGenerationError(error instanceof Error ? error.message : 'Failed');
      setIsGenerating(false);
    }
  };


  return (
    <>
      <Helmet>
        <title>Seller's Pack | UKPropertyChecks</title>
        <meta name="description" content="Prepare solicitor-ready TA6, TA7, and LPE1 forms for your UK property sale. Guided questionnaire, document uploads, official-format PDF. £79 one-off." />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/selling-documents" />
        <meta property="og:title" content="Seller's Pack — UK Property Checks" />
        <meta property="og:description" content="Prepare solicitor-ready TA6, TA7, and LPE1 forms for your UK property sale. £79 one-off." />
        <meta property="og:url" content="https://ukpropertychecks.co.uk/selling-documents" />
      </Helmet>
      <main className="bg-slate-50 min-h-screen">
        {/* Security Trust Strip - visible on all steps */}
        <SecurityTrustStrip />

        {/* Recovery banner for interrupted uploads */}
        {recoveryBanner && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-3" role="status">
            <div className="container-main flex items-center justify-between gap-4">
              <p className="text-sm text-amber-900">Some uploads were interrupted. You can retry them below.</p>
              <button onClick={() => setRecoveryBanner(false)} className="text-amber-700 hover:text-amber-900 flex-shrink-0" aria-label="Dismiss recovery notice">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
        )}
        
        <div className="container-main section-spacing">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
            <div>
              <h1>Seller's Pack</h1>
              <p className="mt-3 text-slate-600">Prepares solicitor-ready TA6, TA7, and LPE1 forms from your answers and uploaded documents.</p>
            </div>
            {(step > 1 || formData.documents.length > 0) && <button onClick={() => setShowClearConfirm(true)} className="text-sm text-slate-600 hover:text-slate-700 underline shrink-0">Start over</button>}
          </div>

          <Card variant="subtle" className="mt-6">
            <p className="text-sm text-slate-700">
              The Law Society's TA6, TA7, and LPE1 forms are the disclosure documents your buyer's solicitor will request. Solicitors typically charge £300–£500 to prepare them.
            </p>
            <p className="mt-3 text-sm text-slate-700">
              Includes TA6{formData.tenure === 'leasehold' ? ', TA7, and LPE1' : ''} as official-format PDFs with section numbering and a source trail showing where each answer came from.
            </p>
            <p className="mt-3 text-xs text-slate-600">
              Forms sourced from the Law Society (2024 edition).
            </p>
          </Card>

          {/* Start over confirmation */}
          {showClearConfirm && (
            <Card className="mt-4 border-red-200">
              <p className="text-sm font-semibold text-slate-950">Are you sure you want to start over?</p>
              <p className="mt-1 text-sm text-slate-600">This will delete all your answers, uploaded documents, and progress. This cannot be undone.</p>
              <div className="mt-4 flex gap-3">
                <button onClick={() => { clearProgress(); setShowClearConfirm(false); }} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg">Yes, start over</button>
                <button onClick={() => setShowClearConfirm(false)} className="btn-secondary">Cancel</button>
              </div>
            </Card>
          )}

          <Stepper currentStep={step} />

          {/* Server-side draft indicator (Phase B3). Sits under the Stepper
              so it's visible from any step the wizard is currently on. */}
          {isAuthenticated && propertyId && draftSavedAt && (
            <div className="mt-4 text-xs text-slate-600" role="status" aria-live="polite">
              {draftSaveError
                ? "Couldn't save your draft just now — we'll retry on the next change."
                : `Draft saved ${new Date(draftSavedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}. Continue from any device.`}
            </div>
          )}

          {/* Reassurance Panel - shown before Step 1 */}
          {step === 1 && (
            <ReassurancePanel 
              title="Before you start"
              items={[
                { icon: 'save', text: 'Your progress is saved automatically' },
                { icon: 'upload', text: 'Upload documents to pre-fill answers' },
                { icon: 'lock', text: 'Your data is encrypted and secure' },
                { icon: 'eye', text: 'Review your answers before paying' }
              ]}
            />
          )}

          {step === 1 && (
            <div className="mt-8 space-y-6">
              {!authLoading && !isAuthenticated && (
                <SignInGate onSignIn={() => navigate('/login', { state: { returnTo: returnToPath } })} />
              )}

              {!formData.address && (
                <Card>
                  <h2 className="text-xl font-semibold">Property address needed</h2>
                  <p className="mt-2 text-sm text-slate-700">
                    Start your Seller&rsquo;s Pack from a property file so we can pre-fill the address and tenure.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={() => navigate('/')}
                      className="btn-secondary"
                    >
                      Go to homepage
                    </button>
                  </div>
                </Card>
              )}

              {formData.address && (
                <Card>
                  <h2 className="text-xl font-semibold">Disclosure questionnaire</h2>
                  <p className="mt-2 text-sm text-slate-700">
                    {formData.address.line1}, {formData.address.postcode} &middot; {formData.tenure === 'leasehold' ? 'Leasehold' : 'Freehold'}
                    {' '}
                    <button
                      type="button"
                      onClick={() => updateFormData({ tenure: formData.tenure === 'leasehold' ? 'freehold' : 'leasehold' })}
                      className="text-xs text-emerald-700 hover:text-emerald-800 underline ml-1"
                    >
                      Change to {formData.tenure === 'leasehold' ? 'Freehold' : 'Leasehold'}
                    </button>
                  </p>
                  <p className="mt-3 text-sm text-slate-700">
                    The Law Society&rsquo;s TA6, TA7, and LPE1 forms are the disclosure documents your buyer&rsquo;s solicitor will request. Solicitors typically charge £300&ndash;£500 to prepare them.
                  </p>
                  <p className="mt-3 text-sm text-slate-700">
                    Answer what you know about the property. Your answers populate your {formData.tenure === 'leasehold' ? 'TA6, TA7, and LPE1 forms' : 'TA6 Property Information Form'}.
                  </p>
                </Card>
              )}

              {formData.address && (
                <DisclosureQuestionnaire
                  answers={formData.disclosureAnswers}
                  onAnswersChange={(answers) => updateFormData({ disclosureAnswers: answers })}
                  tenure={formData.tenure}
                />
              )}

              {formData.address && (
                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <button
                    className="btn-primary w-full sm:w-auto"
                    onClick={() => { trackEvent({ name: 'selling_step_completed', properties: { step: 1 } }); setStep(2); }}
                  >
                    Continue to upload documents
                  </button>
                </div>
              )}
            </div>
          )}


          {step === 2 && (
            <div className="mt-8 space-y-6">
              <Card variant="subtle" padding="sm">
                <div className="flex items-center justify-between text-sm">
                  <div><span className="font-medium text-slate-700">Session expires: </span><span className="text-slate-600">{(() => { const d = new Date(formData.createdAt); d.setDate(d.getDate() + 90); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); })()}</span></div>
                  <span className="text-slate-600 text-xs">{(() => { const d = new Date(formData.createdAt); d.setDate(d.getDate() + 90); return `${Math.ceil((d.getTime() - Date.now()) / 86400000)} days remaining`; })()}</span>
                </div>
              </Card>

              {formData.address && (
                <Card variant="subtle" padding="sm">
                  <p className="text-sm text-slate-700">
                    <span className="font-medium">Property:</span> {formData.address.line1}{formData.address.line2 ? `, ${formData.address.line2}` : ''}, {formData.address.city}, {formData.address.postcode}
                  </p>
                </Card>
              )}

              <Card variant="subtle" padding="sm">
                <p className="text-sm text-slate-700">
                  Uploads help pre-fill form answers and reduce solicitor follow-ups. You can skip this step and add documents later.
                </p>
              </Card>

              {(() => {
                const failed = formData.documents.filter(d => d.status === 'failed');
                if (failed.length === 0) return null;
                const retriable = failed.filter(d => d.retryCount < 3 && d.file);
                return (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm" role="alert">
                    <p className="font-semibold">
                      {failed.length === 1 ? 'A document failed to upload' : `${failed.length} documents failed to upload`}
                    </p>
                    <ul className="mt-2 space-y-1">
                      {failed.map(d => (
                        <li key={d.id}>
                          <span className="font-medium">{d.fileName}</span>
                          {d.error ? ` — ${d.error}` : ''}
                          {d.retryCount >= 3 ? ' (retry limit reached — remove it and add the file again)' : ''}
                        </li>
                      ))}
                    </ul>
                    {retriable.length > 0 && (
                      <button
                        onClick={() => retriable.forEach(d => { void retryUpload(d.id); })}
                        className="mt-3 btn-secondary btn-sm"
                      >
                        {retriable.length === 1 ? 'Retry upload' : 'Retry all'}
                      </button>
                    )}
                  </div>
                );
              })()}

              <DocumentFolder
                tenure={formData.tenure}
                alterationsMade={alterationsMade}
                documents={formData.documents}
                markedUnavailable={markedUnavailable}
                isPaid={packIsPaid}
                onFileSelect={handleFileSelect}
                onMarkUnavailable={handleMarkUnavailable}
                onRemoveFile={removeDocument}
                onRetryUpload={retryUpload}
                onDownloadDocument={packIsPaid ? (docId) => {
                  api.downloadDocument(formData.uploadSessionId, docId).then(({ downloadUrl }) => {
                    window.open(downloadUrl, '_blank');
                  }).catch(() => {});
                } : undefined}
              />

              {markUnavailableError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm" role="alert">
                  <p>{markUnavailableError}</p>
                  <button onClick={() => setMarkUnavailableError(null)} className="mt-1 text-xs text-red-600 hover:text-red-800 underline">Dismiss</button>
                </div>
              )}

              {generationError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm" role="alert">
                  <p className="font-semibold">Generation failed</p>
                  <p className="mt-1">{generationError}</p>
                </div>
              )}

              {!packIsPaid && (() => {
                const visibleDocs = getVisibleDocumentTypes(formData.tenure, alterationsMade);
                const requiredDocs = visibleDocs.filter(dt => dt.required);
                const uploadedDocTypeIds = new Set(formData.documents.filter(d => d.status === 'complete').map(d => d.documentTypeId));
                const requiredComplete = requiredDocs.filter(dt => uploadedDocTypeIds.has(dt.id) || markedUnavailable.has(dt.id)).length;
                const allRequiredDone = requiredComplete === requiredDocs.length;
                const missingRequired = requiredDocs.filter(dt => !uploadedDocTypeIds.has(dt.id) && !markedUnavailable.has(dt.id));

                return (
                  <Card>
                    <h3 className="text-lg font-semibold text-slate-950">Ready to review?</h3>
                    <p className="text-sm text-slate-600 mt-2">
                      {requiredComplete} of {requiredDocs.length} required documents provided.
                      {formData.documents.filter(d => d.status === 'complete').length > 0 && ` ${formData.documents.filter(d => d.status === 'complete').length} total uploaded.`}
                    </p>

                    {!allRequiredDone && missingRequired.length > 0 && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800 font-medium">Missing required documents:</p>
                        <ul className="mt-1 list-disc pl-5 text-sm text-yellow-700">
                          {missingRequired.map(dt => <li key={dt.id}>{dt.name}</li>)}
                        </ul>
                        <p className="text-xs text-yellow-700 mt-2">You can still proceed, but forms may be incomplete.</p>
                      </div>
                    )}

                    <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                      <button className="btn-secondary w-full sm:w-auto" onClick={() => setStep(1)}>Back</button>
                      <button
                        className="btn-primary w-full sm:w-auto"
                        onClick={() => { trackEvent({ name: 'selling_step_completed', properties: { step: 2 } }); setStep(3); void triggerFormatting(); }}
                      >
                        {allRequiredDone ? 'Continue to review' : 'Continue to review (some documents missing)'}
                      </button>
                    </div>
                  </Card>
                );
              })()}
            </div>
          )}

          {step === 3 && (
            <Card className="mt-8">
              <h2 className="text-xl font-semibold">Review and pay</h2>

              {/* Confidence Summary */}
              <div className="mt-6">
                <ConfidenceSummary
                  property={{
                    address: formData.address ? `${formData.address.line1}${formData.address.line2 ? ', ' + formData.address.line2 : ''}, ${formData.address.city}, ${formData.address.postcode}` : 'Address not set',
                    role: formData.role,
                    tenure: formData.tenure
                  }}
                  documentsUploaded={formData.documents.filter(d => d.status === 'complete').length}
                  formsToGenerate={formData.tenure === 'leasehold' ? ['TA6 Property Information Form', 'TA7 Leasehold Information Form', 'LPE1 Leasehold Property Enquiries'] : ['TA6 Property Information Form']}
                  uploadsStep={2}
                  onEditStep={(s) => setStep(s as Step)}
                  onEditProperty={propertyId ? () => navigate(`/account/property/${propertyId}`) : undefined}
                />
              </div>

              <div className="mt-6">
                <FieldReconciliationWizard packId={`PACK-${formData.uploadSessionId}`} />
              </div>

              <Card variant="subtle" padding="sm" className="mt-4"><p className="text-sm text-slate-600">Uploaded documents stored for 90 days. Generated pack available in your account for 12 months.</p></Card>

              {/* Payment option */}
              <div className="mt-6 p-5 bg-emerald-50 border border-emerald-200 rounded-xl">
                <h3 className="font-semibold text-slate-950">Seller's Pack — £79</h3>
                <p className="text-sm text-slate-700 mt-2">
                  Solicitors typically charge £300–£500 to prepare TA6{formData.tenure === 'leasehold' ? ', TA7, and LPE1' : ''}. £79 one-off, no subscription.
                </p>
                <ul className="mt-3 list-disc pl-5 text-sm text-slate-700 space-y-1"><li>Includes TA6{formData.tenure === 'leasehold' ? ', TA7, and LPE1' : ''} as official-format PDFs</li><li>{formData.documents.filter(d => d.status === 'complete').length > 0 ? 'Answers traceable to questionnaire and uploaded documents' : 'Answers based on your questionnaire responses'}</li><li>Available in your account for 12 months</li></ul>
                {generationError && (
                  <div role="alert" className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                    <p className="font-semibold">Payment could not be started</p>
                    <p className="mt-1">{generationError}</p>
                  </div>
                )}
                <button
                  className="btn-primary mt-4"
                  onClick={handleGenerateCompliancePack}
                  disabled={isGenerating}
                  aria-busy={isGenerating}
                >
                  {isGenerating ? 'Redirecting to Stripe...' : 'Buy Seller\'s Pack (£79)'}
                </button>
                <p className="text-xs text-slate-600 mt-2">Payment is handled by Stripe. You'll be redirected to Stripe's secure payment page.</p>
              </div>
              <div className="mt-6"><button className="btn-secondary" onClick={() => setStep(2)}>Back</button></div>
            </Card>
          )}
        </div>
      </main>

      {showTermsModal && (
        <TermsAcceptanceModal
          onAccept={acceptTerms}
          onDecline={declineTerms}
          isAccepting={isAccepting}
          error={termsError}
        />
      )}
    </>
  );
}
